import { useState, useEffect, useCallback, useRef } from "react";
import { DragAndDropSorterContent } from "../../../../../types/newtypes";
import { useScoreTracking } from "../../../../../context/ScoreTrackingContext";

interface UseDragAndDropSorterHookProps {
  content: DragAndDropSorterContent;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  cardId: string;
}

interface UseDragAndDropSorterHookReturn {
  droppedItems: { [categoryId: string]: string[] };
  activeId: string | null;
  isCorrect: boolean;
  showFeedback: boolean;
  feedbackMessage: string;
  handleDragStart: (event: any) => void;
  handleDragEnd: (event: any) => void;
  handleTryAgain: () => void;
  handleMoveToAvailable: (itemId: string) => void;
  getDraggableItems: () => Array<{ id: string; text: string }>;
  getCategories: () => Array<{
    id: string;
    title: string;
    items: string[];
    isComplete: boolean;
  }>;
  getItemCorrectness: (itemId: string) => boolean | null;
}

export const useDragAndDropSorterHook = ({
  content,
  onCardStateChange,
  cardId,
}: UseDragAndDropSorterHookProps): UseDragAndDropSorterHookReturn => {
  // Ensure content has required structure
  const safeContent = {
    ...content,
    items: content?.items || [],
    categories: content?.categories || [],
    max_score: content?.max_score ?? 0,
  };

  const [droppedItems, setDroppedItems] = useState<{
    [categoryId: string]: string[];
  }>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const [itemCorrectness, setItemCorrectness] = useState<Record<string, boolean>>({});

  // Store shuffled items to maintain consistent order across renders
  const [shuffledItems, setShuffledItems] = useState<Array<{ id: string; text: string }>>([]);

  // Score tracking hooks
  const { incrementMistakes, updateCardScore, isCardCompleted, getMistakes } =
    useScoreTracking();

  const maxScore = safeContent.max_score;
  const previousCardIdRef = useRef<string | null>(null);

  // Reset state when cardId changes (for back-to-back drag_and_drop_sorter cards)
  useEffect(() => {
    // Only reset if we're actually transitioning to a different card
    if (
      previousCardIdRef.current !== null &&
      previousCardIdRef.current !== cardId
    ) {
      setDroppedItems({});
      setActiveId(null);
      setIsCorrect(false);
      setShowFeedback(false);
      setFeedbackMessage("");
      setItemCorrectness({});
      setShuffledItems([]);
    }
    previousCardIdRef.current = cardId;
  }, [cardId]);

  // Shuffle items once on mount/content change for stable order
  useEffect(() => {
    const items = [...(safeContent.items || [])];
    // Fisher-Yates shuffle
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    setShuffledItems(items);
  }, [safeContent.items]);

  // Handle drag start
  const handleDragStart = useCallback(
    (event: any) => {
      setActiveId(event?.active?.id);
    },
    [],
  );

  // Handle drag end with immediate evaluation
  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || !active || !over?.id?.startsWith("category-")) {
        return;
      }

      const categoryId = over?.id;
      const itemId = active?.id;

      setDroppedItems((prev) => {
        const newItems = { ...prev };

        Object.keys(newItems).forEach((catId) => {
          newItems[catId] = newItems[catId]?.filter((id) => id !== itemId);
        });

        if (!newItems[categoryId]) {
          newItems[categoryId] = [];
        }
        newItems[categoryId]?.push(itemId);
        return newItems;
      });

      const categoryIndex = parseInt(categoryId.replace("category-", ""));
      const category = safeContent?.categories[categoryIndex];
      const isCorrectPlacement = category?.correct_item_ids?.includes(itemId);

      setItemCorrectness((prev) => ({ ...prev, [itemId]: Boolean(isCorrectPlacement) }));

      if (isCorrectPlacement) {
        // For correct individual drops, show only positive item feedback via category styling.
        // Do NOT show overall "Keep going" here; that happens when all items are correct.
        setShowFeedback(false);
        setFeedbackMessage("");
      } else {
        setShowFeedback(true);
        setIsCorrect(false);
        setFeedbackMessage(safeContent?.feedback?.incorrect_text || "Keep going");

        if (!isCardCompleted(cardId)) {
          incrementMistakes(cardId, maxScore);
        }
      }
    },
    [incrementMistakes, isCardCompleted, cardId, maxScore, safeContent?.categories, safeContent?.feedback?.incorrect_text],
  );

  useEffect(() => {
    const totalItems = safeContent?.items?.length || 0;
    const correctItems = Object.entries(itemCorrectness).filter(
      ([, value]) => value,
    ).length;

    if (totalItems > 0 && correctItems === totalItems) {
      setIsCorrect(true);
      setShowFeedback(true);
      setFeedbackMessage(safeContent?.feedback?.correct_text || "Great work!");
      onCardStateChange?.(true, true);

      if (!isCardCompleted(cardId)) {
        const mistakes = getMistakes(cardId) || 0;
        updateCardScore(cardId, mistakes, maxScore);
      }
    } else {
      onCardStateChange?.(false, false);
    }
  }, [itemCorrectness, safeContent?.items?.length, safeContent?.feedback?.correct_text, onCardStateChange, cardId, getMistakes, updateCardScore, maxScore, isCardCompleted]);

  const handleTryAgain = useCallback(() => {
    setIsCorrect(false);
    setShowFeedback(false);
    setFeedbackMessage("");

    setDroppedItems((prev) => {
      const newItems: { [categoryId: string]: string[] } = {};

      Object.entries(prev)?.forEach(([categoryId, itemIds]) => {
        const correctItems = itemIds?.filter((itemId) => {
          const categoryIndex = parseInt(categoryId?.replace("category-", ""));
          const category = safeContent?.categories[categoryIndex];
          return category?.correct_item_ids?.includes(itemId);
        });

        if (correctItems?.length > 0) {
          newItems[categoryId] = correctItems;
        }
      });

      return newItems;
    });

    setItemCorrectness((prev) => {
      const lockedItems = Object.entries(prev).filter(([, value]) => value);
      return Object.fromEntries(lockedItems);
    });

    onCardStateChange?.(false, false);
  }, [onCardStateChange, safeContent?.categories]);

  const handleMoveToAvailable = useCallback(
    (itemId: string) => {
      // Hide any error feedback when user starts correcting placements
      setShowFeedback(false);
      setFeedbackMessage("");

      setDroppedItems((prev) => {
        const newItems = { ...prev };
        Object.keys(newItems)?.forEach((categoryId) => {
          newItems[categoryId] = newItems[categoryId]?.filter((id) => id !== itemId);
        });
        return newItems;
      });

      setItemCorrectness((prev) => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
    },
    [],
  );

  const getItemCorrectness = useCallback(
    (itemId: string) => {
      if (itemCorrectness[itemId] === undefined) {
        return null;
      }
      return itemCorrectness[itemId];
    },
    [itemCorrectness],
  );

  const getDraggableItems = useCallback(() => {
    // Filter out dropped items from pre-shuffled list to maintain stable order
    const droppedItemIds = Object.values(droppedItems).flat();
    return shuffledItems.filter(
      (item) => !droppedItemIds?.includes(item.id)
    );
  }, [droppedItems, shuffledItems]);

  const getCategories = useCallback(() => {
    return safeContent?.categories.map((category, index) => {
      const categoryId = `category-${index}`;
      const items = droppedItems[categoryId] || [];
      const correctIds = category?.correct_item_ids || [];
      const placedCorrect = items.filter((itemId) => correctIds.includes(itemId));
      const isComplete =
        correctIds.length > 0 &&
        placedCorrect.length === correctIds.length &&
        items.length === correctIds.length;

      return {
        id: categoryId,
        title: category?.title,
        items,
        isComplete,
      };
    });
  }, [safeContent?.categories, droppedItems]);

  useEffect(() => {
    setDroppedItems({});
    setActiveId(null);
    setIsCorrect(false);
    setShowFeedback(false);
    setFeedbackMessage("");
    setItemCorrectness({});
  }, [safeContent?.items?.length, safeContent?.categories.length]);

  return {
    droppedItems,
    activeId,
    isCorrect,
    showFeedback,
    feedbackMessage,
    handleDragStart,
    handleDragEnd,
    handleTryAgain,
    handleMoveToAvailable,
    getDraggableItems,
    getCategories,
    getItemCorrectness,
  };
};
